import React, { memo, useMemo, useState } from "react"
import IOrder from "../../../interfaces/IOrder"
import { CampingStyleProps, CampProps } from "../../../interfaces"
import classNames from "../../../utils/classNames"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import IOrderPreview from "../../../interfaces/IOrderPreview"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import parseDateTime from "../../../utils/parseDateTime"
import { LONG_TERM_RESERVATION_KINDS, longTermReservationKind, RESERVATION_KINDS } from "~/constants"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { Transition } from "@headlessui/react"
import dynamicRound from "~/utils/dynamicRound"

const OrderSummary = ({
  order,
  camp,
  isLoading,
  isIncludingLineItems,
  isPreview,
  isHidingServiceFee,
  isInvoice,
  noBackground,
  newReservationFlow,
  showProcessingFeesLabel,
  taxRateLabel,
  isGuestView,
  isDeposit,
  isInvoicePage,
  isRecurring,
}: {
  order: IOrder | IOrderPreview | null
  camp: CampProps
  isLoading?: boolean
  isIncludingLineItems?: boolean
  isPreview?: boolean
  isHidingServiceFee?: boolean
  isInvoice?: boolean
  noBackground?: boolean
  newReservationFlow?: { showTitle: boolean; isRecurring: boolean; recurringKind: longTermReservationKind }
  showProcessingFeesLabel?: boolean
  taxRateLabel?: string
  isGuestView?: boolean
  isDeposit?: boolean
  isInvoicePage?: boolean
  isRecurring?: boolean
}) => {
  const backgroundColor = noBackground ? "bg-transparent" : "bg-gray-50"

  const getAmountDueValue = () => {
    if (isInvoice) {
      return "unpaid_total_without_service_fee_in_cents" in order && order?.unpaid_total_without_service_fee_in_cents
        ? order?.unpaid_total_without_service_fee_in_cents
        : order?.unpaid_total_in_cents
    } else if (isPreview) {
      return isHidingServiceFee
        ? (order as IOrderPreview)?.unpaid_total_without_service_fee_in_cents
        : order?.unpaid_total_in_cents
    } else {
      return null
    }
  }

  const amountDueValue = getAmountDueValue()

  if (isLoading || !order) {
    return (
      <div
        className={classNames(
          backgroundColor,
          "p-4 rounded-md whitespace-nowrap",
          newReservationFlow?.isRecurring && "py-6",
        )}
      >
        <div className="w-full animate-pulsing flex space-x-4">
          <div className="w-full flex-1 space-y-2 py-1">
            <div className="space-y-3">
              <div className="mb-2 w-full grid grid-cols-6 gap-2">
                <div
                  className={classNames(
                    "w-full rounded-md bg-gray-200 col-span-3",
                    newReservationFlow?.isRecurring ? "h-6 py-3" : "h-3",
                  )}
                />
                <div className="col-span-2" />
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
              </div>
              <div
                className={classNames(
                  "mb-2 w-full grid grid-cols-6 ",
                  (newReservationFlow?.isRecurring && "space-y-2") || "gap-2",
                )}
              >
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
                <div className="col-span-4" />
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
              </div>
              <div className="mb-2 w-full grid grid-cols-6 gap-2">
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-2" />
                <div className="col-span-3" />
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
              </div>
            </div>
            <div className="mt-3 mb-4 w-full h-0.25 bg-gray-50" />
            <div className="space-y-2">
              <div className="mb-2 w-full grid grid-cols-6 gap-2">
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
                <div className="col-span-4" />
                <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
              </div>
            </div>
            <div className="mt-3 mb-6 w-full h-0.25 bg-gray-50" />
            <div className="w-full grid grid-cols-6 gap-4">
              <div className="h-3 w-full rounded-md bg-gray-200 col-span-3" />
              <div className="col-span-2" />
              <div className="h-3 w-full rounded-md bg-gray-200 col-span-1" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames(backgroundColor, "p-4 rounded-md whitespace-nowrap")}>
      {isIncludingLineItems && <LineItems order={order} camp={camp} />}
      <div>
        <HeaderElement isPreview={isPreview} newReservationFlow={newReservationFlow} />
        <OrderRow
          label={isDeposit ? "Deposit" : "Subtotal"}
          value={formatMoneyFromCents(order?.subtotal_in_cents, camp.currency)}
          className={`${!isIncludingLineItems && "mt-0"} ${!order?.discounted_subtotal_in_cents && !order?.paid_subtotal_in_cents && "font-bold"}`}
        />

        <DiscountsAndPaidAmounts order={order} camp={camp} showProcessingFeesLabel={showProcessingFeesLabel} />

        <TaxesAndFees
          order={order}
          isPreview={isPreview}
          isInvoice={isInvoice}
          camp={camp}
          isHidingServiceFee={isHidingServiceFee}
          showProcessingFeesLabel={showProcessingFeesLabel}
          taxRateLabel={taxRateLabel}
          isGuestView={isGuestView}
          isInvoicePage={isInvoicePage}
          isRecurring={newReservationFlow?.isRecurring || isRecurring}
        />
      </div>
      <Divider />
      <OrderRow
        label={"Amount due"}
        value={formatMoneyFromCents(amountDueValue, camp.currency)}
        isVisible={isPreview || isInvoice}
        className="font-bold"
      />
      {!isPreview && !isInvoice && (
        <OrderRow
          label={"Total price"}
          className="font-bold"
          value={formatMoneyFromCents(order?.total_in_cents, camp.currency)}
        />
      )}
    </div>
  )
}

const DiscountsAndPaidAmounts = ({
  order,
  camp,
  showProcessingFeesLabel = false,
}: {
  order: IOrder
  camp: CampProps
  showProcessingFeesLabel: boolean
}) => {
  return (
    <>
      {order?.discount_in_cents > 0 && (
        <OrderRow
          label={`Discount (${order?.discount_name})`}
          value={"-" + formatMoneyFromCents(order?.discount_in_cents, camp.currency)}
          labelClasses="truncate"
          hasTooltip
        />
      )}
      {order?.discounted_subtotal_in_cents > 0 && (
        <OrderRow
          label={"Discounted subtotal"}
          value={formatMoneyFromCents(order?.discounted_subtotal_in_cents, camp.currency)}
          className={!order?.paid_total_in_cents && "font-bold"}
        />
      )}
      {order?.paid_tax_fee_in_cents > 0 && (
        <OrderRow label={"Paid tax"} value={formatMoneyFromCents(order.paid_tax_fee_in_cents, camp.currency)} />
      )}
      {order?.paid_credit_card_fee_in_cents > 0 && (
        <OrderRow
          label={(showProcessingFeesLabel && "Paid processing fee") || "Paid credit card fee"}
          value={formatMoneyFromCents(order.paid_credit_card_fee_in_cents, camp.currency)}
        />
      )}
      {order?.paid_service_fee_in_cents > 0 && (
        <OrderRow
          label={"Paid service fee"}
          value={formatMoneyFromCents(order.paid_service_fee_in_cents, camp.currency)}
        />
      )}
      {order?.paid_total_in_cents > 0 && (
        <OrderRow
          label={order?.paid_at ? `Paid on ${parseDateTime(order?.paid_at).toFormat("MMM d, yyyy")}` : "Paid amount"}
          value={`- ${formatMoneyFromCents(order?.paid_total_in_cents, camp.currency)}`}
        />
      )}
      {order?.paid_subtotal_in_cents > 0 && (
        <OrderRow
          label={"Remaining subtotal"}
          className="font-bold"
          value={formatMoneyFromCents(order?.unpaid_subtotal_in_cents, camp.currency)}
        />
      )}
    </>
  )
}

const BundledFeesLabel = ({
  camp,
  order,
  showProcessingFeesLabel = false,
  taxRateLabel,
  isRecurring
}: {
  order: IOrder
  camp: CampProps
  showProcessingFeesLabel: boolean
  taxRateLabel?: string
  isRecurring?: boolean
}) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false)

  return (
    <div className="flex items-start">
      {order.tax_fee_in_cents > 0 ? "Taxes & Fees" : "Fees"}
      <div className="relative">
        <InformationCircleIcon
          className="w-3.5 ml-0.5 cursor-pointer"
          onMouseEnter={() => setIsInfoOpen(true)}
          onMouseLeave={() => setIsInfoOpen(false)}
        />
        <Transition
          className="price-details-info bg-white rounded-md absolute"
          show={isInfoOpen}
          enter="transition-opacity duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="wrapper text-sm whitespace-nowrap py-4 px-2 flex flex-col gap-y-2">
            {order.tax_rate > 0 ? (
              <div className="w-full flex">
                <div>
                  {taxRateLabel ? taxRateLabel : "Taxes Collected"} ({dynamicRound(order.tax_rate * 100)}%):
                </div>
                &nbsp;
                <div className="text-right">{formatMoneyFromCents(order.tax_fee_in_cents, camp.currency)}</div>
              </div>
            ) : (
              <></>
            )}
            {order.credit_card_rate > 0 ? (
              <div className="w-full flex">
                <div>
                  {(showProcessingFeesLabel && "Processing fee") || "Credit card fees"} (
                  {dynamicRound(order.credit_card_rate * 100)}%):
                </div>
                <div className="text-right">
                  &nbsp;{formatMoneyFromCents(order.credit_card_fee_in_cents, camp.currency)}
                </div>
              </div>
            ) : (
              <></>
            )}

            {!((!isRecurring && order?.is_absorbing_service_fee) ||
              (isRecurring && order?.is_absorbing_service_fee_for_recurring)) && (
                <div className="w-full flex">
                  <div>Service Fee: &nbsp;</div>
                  <div className="text-right">{formatMoneyFromCents(order.service_fee_in_cents, camp.currency)}</div>
                </div>
              )}
          </div>
        </Transition>
      </div>
    </div>
  )

}

const TaxesAndFees = ({
  order,
  isInvoice,
  camp,
  isHidingServiceFee,
  isPreview,
  showProcessingFeesLabel,
  taxRateLabel,
  isGuestView,
  isInvoicePage,
  isRecurring
}: {
  order: IOrder
  isInvoice: boolean
  camp: CampProps
  isHidingServiceFee: boolean
  isPreview: boolean
  showProcessingFeesLabel?: boolean
  taxRateLabel?: string
  isGuestView?: boolean
  isInvoicePage?: boolean
  isRecurring?: boolean
}) => {
  const isAbsorbingServiceFee =
    (!isRecurring && order?.is_absorbing_service_fee) || (isRecurring && order?.is_absorbing_service_fee_for_recurring)
  const hasAnyFees =
    !isAbsorbingServiceFee &&
    (order?.unpaid_tax_fee_in_cents > 0 ||
      order?.credit_card_fee_in_cents > 0 ||
      (order?.service_fee_in_cents > 0 && !isInvoice && !isHidingServiceFee))

  if (isPreview) {
    if (camp.is_bundling_fees_for_guest && isGuestView && !isInvoicePage) {
      const realSubtotalInCents = order?.discounted_subtotal_in_cents
        ? order?.discounted_subtotal_in_cents
        : order?.subtotal_in_cents
      const unpaidFeesInCents = order?.unpaid_total_in_cents - realSubtotalInCents
      return (
        <OrderRow
          label={
            <BundledFeesLabel
              order={order}
              camp={camp}
              showProcessingFeesLabel={showProcessingFeesLabel}
              taxRateLabel={taxRateLabel}
              isRecurring={isRecurring}
            />
          }
          value={formatMoneyFromCents(unpaidFeesInCents, camp.currency)}
          isVisible={hasAnyFees}
        />
      )
    } else {
      return (
        <>
          {hasAnyFees && <Divider />}
          {order?.unpaid_tax_fee_in_cents > 0 && (
            <>
              <OrderRow
                label={`Tax (${(((order.tax_rate && order.tax_rate) || Number(order.line_items.find((li) => li.reservation_id).tax_rate)) * 100).toFixed(2)}%)`}
                value={formatMoneyFromCents(order?.unpaid_tax_fee_in_cents, camp.currency)}
              />
            </>
          )}
          {order?.credit_card_fee_in_cents > 0 && (
            <OrderRow
              label={`${(showProcessingFeesLabel && "Processing fee") || "Credit card fee"}`}
              value={formatMoneyFromCents(order?.credit_card_fee_in_cents, camp.currency)}
            />
          )}
          {order?.service_fee_in_cents > 0 && !isInvoice && !isHidingServiceFee && (
            <ServiceFees
              className={
                ((!isRecurring && order?.is_absorbing_service_fee) ||
                  (isRecurring && order?.is_absorbing_service_fee_for_recurring)) &&
                "text-gray-400"
              }
              order={order}
              camp={camp}
              isGuestView={isGuestView}
              isRecurring={isRecurring}
            />
          )}
        </>
      )
    }
  } else if (order?.unpaid_tax_fee_in_cents > 0) {
    return (
      <>
        <Divider />
        <OrderRow label={`Tax`} value={formatMoneyFromCents(order?.unpaid_tax_fee_in_cents, camp.currency)} />
      </>
    )
  } else {
    return <></>
  }
}

const ServiceFees = ({
  order,
  camp,
  className,
  isGuestView,
  isRecurring
}: {
  order: IOrder
  camp: CampProps
  isGuestView: boolean
  isInvoicePage?: boolean
  className?: string
  isRecurring?: boolean
}) => {
  const isAbsorbingServiceFee =
    (!isRecurring && order?.is_absorbing_service_fee) || (isRecurring && order?.is_absorbing_service_fee_for_recurring)
  if (isGuestView && isAbsorbingServiceFee) {
    return <></>
  }

  return (
    <OrderRow
      className={className}
      label={
        <div>
          Service fee
          {isAbsorbingServiceFee && <span className="text-xs ml-1">(Not included in total)</span>}
        </div>
      }
      value={formatMoneyFromCents(order?.service_fee_in_cents, camp.currency)}
    />
  )
}

const OrderRow = ({
  label,
  value,
  isVisible,
  className,
  labelClasses,
  hasTooltip,
}: {
  label: string | React.ReactNode
  value: string
  isVisible?: boolean
  className?: string
  labelClasses?: string
  hasTooltip?: boolean
}) => {
  if (isVisible !== undefined && !isVisible) return <></>

  return (
    <div className={classNames("py-1 flex justify-between w-full", className)}>
      <div className={labelClasses} title={typeof label === "string" && hasTooltip ? label : undefined}>
        {label}
      </div>
      <div>{value}</div>
    </div>
  )
}

const LineItems = ({ order, camp }: { order: IOrder; camp: CampProps }) => {
  return (
    <div>
      {order?.line_items?.filter((li) => !(li.total_amount_in_cents === 0 && li.kind === LINE_ITEM_KINDS.BASIC && li.label.toLocaleLowerCase() === 'reservation'))?.map((lineItem) => (
        <div className="mb-3 flex justify-between items-center w-full">
          <div className="flex flex-col">
            <label htmlFor="name" className="flex items-center text-base text-gray-900">
              <span>{lineItem.label}</span>
              {(lineItem.quantity > 0 || lineItem.kind === LINE_ITEM_KINDS.ELECTRIC) && (
                <span className="ml-1">x {formatMoneyFromCents(lineItem.unit_amount_in_cents, camp.currency)}</span>
              )}
              <span className="ml-1">
                x {lineItem.quantity}
                {lineItem.kind === LINE_ITEM_KINDS.ELECTRIC ? " kWh" : ""}
              </span>
              {Number(lineItem.occurrences) > 1 && (
                <span className="ml-1">
                  x {lineItem.occurrences} {lineItem.occurrence_unit}
                </span>
              )}
            </label>
          </div>
          <span className="text-gray-900">
            <span>{formatMoneyFromCents(lineItem.total_amount_in_cents, camp.currency)}</span>
          </span>
        </div>
      ))}
      <Divider />
    </div>
  )
}

const Divider = () => {
  return <div className="my-2 w-full h-0.25 bg-gray-300" />
}
interface HeaderElementProps {
  newReservationFlow?: {
    showTitle?: boolean
    isRecurring?: boolean
    recurringKind?: string
  }
  isPreview?: boolean
}

const HeaderElement: React.FC<HeaderElementProps> = memo(({ newReservationFlow, isPreview }) => {
  return newReservationFlow && newReservationFlow.showTitle && isPreview && newReservationFlow?.isRecurring ? (
    <div className="text-lg font-bold mb-4">
      {newReservationFlow?.recurringKind === LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY ||
        newReservationFlow?.recurringKind === LONG_TERM_RESERVATION_KINDS.SEASONAL_ONLY
        ? "Season price"
        : "Monthly price"}
    </div>
  ) : null
})

export default OrderSummary
